/**
 * One name for a section on every surface (Builder Coherence BC-14, F4).
 *
 * The section list, the command bar, the canvas nameplate, the agent's context
 * chips, the undo toasts and the history timeline each had a ladder of their
 * own, so one section read "Hero", "hero", "Content Left" and "stitch-1033"
 * depending on where you looked. The ladder is `resolveSectionName` in
 * shared/section-categories.js; this module supplies the category map to the
 * surfaces that never had one.
 *
 * `data/category-map.json` is fetched once, through core/asset-loader.js,
 * imported DYNAMICALLY so pure modules (agent-context-refs, section-categories'
 * consumers in node tests) can import the formatter without pulling in the
 * loader and its side effects.
 */

import { resolveSectionName } from './section-categories.js';

let cachedMap = null;
let pending = null;

/**
 * The category map, fetched once. A failed fetch resolves `{}` and is retried
 * on the next call, so a bad afternoon costs names ("Call To Action" instead
 * of "Call to Action"), never a render.
 *
 * @param {{load?: (key: string) => Promise<object>}} [deps]
 * @returns {Promise<object>}
 */
export function getCategoryMap({ load } = {}) {
  if (cachedMap) return Promise.resolve(cachedMap);
  if (!pending) {
    pending = (async () => {
      const loader = load || (await import('../core/asset-loader.js')).loadJSON;
      const map = await loader('data/category-map.json');
      cachedMap = map && typeof map === 'object' ? map : {};
      return cachedMap;
    })().catch(() => {
      // Expected-flow fallback: asset-loader already reports the failed fetch
      // (silently, as a boot file); the ladder's title-cased rung covers it.
      pending = null;
      return {};
    });
  }
  return pending;
}

/** The map if it has loaded, else null. Never fetches. */
export function peekCategoryMap() {
  return cachedMap;
}

/** Seed the cache from a map a surface already holds (and tests). */
export function primeCategoryMap(map) {
  cachedMap = map && typeof map === 'object' ? map : null;
  pending = null;
}

/**
 * The section's display name through the shared ladder, with the cached map
 * when the caller has none.
 *
 * @param {object} section
 * @param {{categoryMap?: object, derivedName?: string, fallback?: string}} [opts]
 *   `fallback` replaces the ladder's generic "Section" — the canvas names an
 *   unclassified shared section "Navigation" or "Footer" by its slot.
 * @returns {string}
 */
export function formatSectionName(section, { categoryMap, derivedName, fallback } = {}) {
  const resolved = resolveSectionName(section, categoryMap || cachedMap || {}, { derivedName });
  return resolved.source === 'fallback' && fallback !== undefined && section?.type !== 'freeform'
    ? fallback
    : resolved.name;
}
